import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { Dialog, useDialog } from "@swa-ui/core";
import { Form, useForm, yupResolver } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

import { AuthContext } from "../../AuthProvider";
import { MissingCorporateInformationLink } from "../MissingCorporateInformationLink";
import { MissingInformationForm } from "../MissingInformationForm";
import { getMissingInformationFormSchema } from "../MissingInformationForm/MissingInformationForm.schema";
import { MissingInformationSaveStatus } from "../MissingInformationSaveStatus";
import { useMissingCorporateInformationServices } from "./useMissingCorporateInformationServices";
import { useMissingInformationServices } from "./useMissingInformationServices";

const MISSING_INFORMATION_DISPLAY_ORDER = ["EMAIL", "PHONE", "USERNAME", "SECURITY_CHALLENGE"];
const MISSING_STEP_ENTER_INFORMATION = "MISSING_STEP_ENTER_INFORMATION";
const MISSING_STEP_CORPORATE_LINK = "MISSING_STEP_CORPORATE_LINK";
const MISSING_STEP_STATUS = "MISSING_STEP_STATUS";

/**
 * MissingInformationDialog is a dialog that prompts the user to enter missing information and
 * displays a success or error message.
 */
export const MissingInformationDialog = (props) => {
  const {
    missingCorporateInformation = [],
    missingInformation: unsortedMissingInformation,
    missingInformationServices,
    missingCorporateInformationServices,
  } = props;
  const { screenSize } = useDeviceInfo();
  const { loading, saveMissingInformation } =
    missingInformationServices ?? useMissingInformationServices();
  const { loading: loadingSaveMissingCorporateInformation, saveMissingCorporateInformation } =
    missingCorporateInformationServices ?? useMissingCorporateInformationServices();
  const { auth, handleLogout, handleTokenExchange } = useContext(AuthContext);
  const missingInformation = MISSING_INFORMATION_DISPLAY_ORDER.filter((item) =>
    unsortedMissingInformation.includes(item)
  );
  const [currentStep, setCurrentStep] = useState(
    missingInformation.length === 0 && missingCorporateInformation.length > 0
      ? MISSING_STEP_CORPORATE_LINK
      : MISSING_STEP_ENTER_INFORMATION
  );
  const [usernameAvailabilityStatus, setUsernameAvailabilityStatus] = useState();

  const formMethods = useForm({
    context: {
      usernameAvailabilityStatus,
    },
    resolver: yupResolver(getMissingInformationFormSchema(missingInformation)),
  });
  const { isDialogVisible } = useDialog(
    "center",
    missingInformation ? "above" : "hidden",
    handleClose
  );
  const [unsavedInformation, setUnsavedInformation] = useState({});

  return (
    isDialogVisible && (
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Dialog {...getDialogProps()}>
          {currentStep === MISSING_STEP_ENTER_INFORMATION && (
            <MissingInformationForm
              loading={loading}
              missingInformation={missingInformation}
              onUsernameAvailabilityStatusChange={setUsernameAvailabilityStatus}
            />
          )}
          {currentStep === MISSING_STEP_STATUS && (
            <MissingInformationSaveStatus
              accountNumber={auth["customers.userInformation.accountNumber"]}
              missingInformation={missingInformation}
              unsavedInformation={unsavedInformation}
            />
          )}
          {currentStep === MISSING_STEP_CORPORATE_LINK && (
            <MissingCorporateInformationLink
              accountNumber={auth["customers.userInformation.accountNumber"]}
              companyName={auth["corporate.companyUserInformation.name"]}
              missingCorporateInformation={missingCorporateInformation}
            />
          )}
        </Dialog>
      </Form>
    )
  );

  function getDialogProps() {
    return {
      buttonGroupProps: getButtonGroupProps(),
      onClose: handleClose,
      portal: false,
      revealed: true,
      showClose: true,
    };
  }

  function getButtonGroupProps() {
    const buttons = {
      MISSING_STEP_CORPORATE_LINK: {
        buttonList: [
          {
            label: i18n("MissingInformationDialog__RETURN_TO_LOGIN"),
            props: {
              onClick: handleLogout,
              styleType: "secondary",
              type: "button",
            },
          },
          {
            label: i18n("MissingInformationDialog__CORRECT"),
            props: {
              disabled: loadingSaveMissingCorporateInformation,
              onClick: handleSubmitCorporateLinkAccount,
              styleType: "primary",
              type: "button",
            },
          },
        ],
      },
      MISSING_STEP_ENTER_INFORMATION: {
        buttonList: [
          {
            label: i18n("MissingInformationDialog__CANCEL"),
            props: {
              disabled: loading,
              onClick: handleClose,
              styleType: "secondary",
              type: "button",
            },
          },
          {
            label: i18n("MissingInformationDialog__SAVE"),
            props: {
              name: "save",
              styleType: "primary",
              submitInProgress: loading,
              type: "submit",
            },
          },
        ],
        equalSize: screenSize === "small",
      },
      MISSING_STEP_STATUS: {
        buttonList: [
          {
            label: i18n("MissingInformationDialog__OKAY"),
            props: {
              onClick: handleClose,
              styleType: "primary",
              type: "button",
            },
          },
        ],
      },
    };

    return {
      "aria-label-next": i18n("MissingInformationDialog__BUTTON_GROUP_ARIA_NEXT"),
      "aria-label-previous": i18n("MissingInformationDialog__BUTTON_GROUP_ARIA_PREVIOUS"),
      ...buttons[currentStep],
    };
  }

  async function handleSubmit(missingFormData) {
    const { callStatus } = await saveMissingInformation(missingFormData);

    if (callStatus.ok) {
      setUnsavedInformation(callStatus.unsavedInformation);
    } else {
      setUnsavedInformation(missingInformation);
    }
    setCurrentStep(MISSING_STEP_STATUS);
  }

  async function handleClose() {
    if (currentStep === MISSING_STEP_STATUS) {
      if (unsavedInformation) {
        handleLogout();
      } else if (missingCorporateInformation.length === 0) {
        handleTokenExchange(auth.id_token);
      } else {
        setCurrentStep(MISSING_STEP_CORPORATE_LINK);
      }
    } else {
      handleLogout();
    }
  }

  async function handleSubmitCorporateLinkAccount() {
    const { callStatus } = await saveMissingCorporateInformation();

    if (callStatus.ok) {
      handleTokenExchange(auth.id_token);
    } else {
      handleLogout();
    }
  }
};

MissingInformationDialog.propTypes = {
  /** Missing corporate information to be collected */
  missingCorporateInformation: PropTypes.array,

  /** Services to save corporate missing information */
  missingCorporateInformationServices: PropTypes.shape({
    saveMissingCorporateInformation: PropTypes.func.isRequired,
  }),

  /** Missing information to be collected */
  missingInformation: PropTypes.array.isRequired,

  /** Services to save missing information */
  missingInformationServices: PropTypes.shape({
    saveMissingInformation: PropTypes.func.isRequired,
  }),
};
